import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { ChatAttachmentView, ChatDialogEvent, ChatDialogEventType, ChatTextView, spacesCorrection } from 'sdk';
import { linksGen } from 'sdk';

import { useSDK } from '../../../../app';
import { commonMessages } from '../../../../i18n/commonMessages';
import { styled } from '../../../../styled';
import type { Theme } from '../../../../theme/interface/detailed';
import { useSanitize } from '../../../../utils/useSanitize';
import { useShouldDisplayOperatorInfo } from '../../../../utils/useShouldDisplayOperatorInfo';
import { ReplyMessageScope } from '../../../forms/fields/messageField/reply/ReplyMessageScope';
import { AttachmentPreview } from '../attachmentPreview';
import { Message } from '../message';
import { MessageContent } from '../messageContent';
import { MessageTime } from '../messageTime';
import { AgentName } from './agentName';
import { AvatarWrapper } from './avatarWrapper';
import { MessageAvatar } from './messageAvatar';

type MessageType = ChatDialogEvent & { view: ChatTextView };
type Interface = {
	message: MessageType;
	hideAvatar?: boolean;
	className: string;
	isInvolve?: boolean;
};

const options = ['DIALOG', 'HEADER_AND_DIALOG', 'HEADER_AND_DIALOG_OVERRIDES_STATIC_HEADER'];

const styles = css`
	display: inline-block;
	float: left;
	max-width: 92.5%;
	padding-bottom: 8px;

	& > div {
		display: flex;
	}
`;

const Component = observer(({ message, hideAvatar, className, isInvolve = false }: Interface): ReactElement => {
	const { view, creationTimestamp, accountId, id, replyTo } = message;
	const { accountsStore } = useSDK();
	const isDisplayAgentInfo = useShouldDisplayOperatorInfo(options);
	const text = useSanitize(
		view.text ? spacesCorrection(view.contentType !== 'text/html' ? linksGen(view.text) : view.text) : ''
	);
	const agentInfo = accountId && accountsStore.getAccountInfo(accountId);
	const { formatMessage } = useIntl();

	return (
		<div id={`ncc-message-${id}`} className={className}>
			<div>
				{isDisplayAgentInfo && !hideAvatar && (
					<AvatarWrapper>
						<MessageAvatar accountId={accountId} />
					</AvatarWrapper>
				)}
				<MessageContent withLeftMargin={isDisplayAgentInfo && !!hideAvatar}>
					{isDisplayAgentInfo && !hideAvatar && (
						<AgentName>
							{(agentInfo && agentInfo.showName) || formatMessage(commonMessages.operator)}
						</AgentName>
					)}
					<Message message={message} isInvolve={isInvolve}>
						{replyTo && <ReplyMessageScope fromClient={false} message={replyTo} />}
						{view.type === ChatDialogEventType.Attach ? (
							<AttachmentPreview
								fromClient={false}
								id={(view as ChatAttachmentView).attachId as number}
							/>
						) : (
							text && <div dangerouslySetInnerHTML={text} />
						)}
						<MessageTime fromClient={false} date={creationTimestamp} />
					</Message>
				</MessageContent>
			</div>
		</div>
	);
});

const AgentMessageContainer = styled(Component)(
	({
		theme: {
			chat: {
				messages: { messageContainer },
			},
		},
	}: {
		theme: Theme;
	}) => messageContainer,
	styles
);

export { AgentMessageContainer };
